import { formatInTimeZone } from 'date-fns-tz'
import { AllocationInviteMode } from '@/Network/Types/Requests/AllocationInviteRequests'
import type {
  AllocationInvitePreviewRequest,
  CreateAllocationInviteRequest
} from '@/Network/Types/Requests/AllocationInviteRequests'
import type { ChangeReasons } from '@/Network/Types/Requests/ChangeReasonRequests'
import type {
  AllocationInviteIssueReason,
  AllocationInvitePreview,
  PendingCommitment
} from '@/Network/Types/Responses/AllocationInviteResponses'

/** Passo 2: "Enviar convite" ou "Alocar sem convite" (D27, D31). */
export type FillMode = 'INVITE' | 'DIRECT'
/** Passo 2, substituição: o médico atual fica até o aceite ou sai agora (plano WEB-1, P1). */
export type ReplacementChoice = 'KEEP' | 'NOW'

export const pluralizeStations = (n: number): string => `${n} ${n === 1 ? 'plantão' : 'plantões'}`
export const pluralizeDoctors = (n: number): string => `${n} ${n === 1 ? 'médico' : 'médicos'}`

export function resolveInviteMode(
  hasCurrentDoctor: boolean,
  replacement: ReplacementChoice
): AllocationInviteMode {
  if (!hasCurrentDoctor) return AllocationInviteMode.VACANCY
  return replacement === 'NOW'
    ? AllocationInviteMode.REPLACE_NOW
    : AllocationInviteMode.REPLACE_KEEP
}

/** Só o `REPLACE_NOW` tira o médico atual na criação do convite: é o único que pede motivo (D104). */
export function inviteRemovesCurrentDoctorNow(mode: AllocationInviteMode): boolean {
  return mode === AllocationInviteMode.REPLACE_NOW
}

/**
 * Teto de plantões por convite: `ALLOCATION_INVITE_MAX_STATIONS` do backend (`ArrayMaxSize` do
 * `stationIds` na prévia e na criação). D140: o teto fica e o assistente avisa antes da prévia.
 */
export const ALLOCATION_INVITE_MAX_STATIONS = 100

/** Frase do bloqueio (D28: linguagem simples, diz o que fazer). */
export const INVITE_STATION_LIMIT_MESSAGE = `Um convite pode ter no máximo ${ALLOCATION_INVITE_MAX_STATIONS} plantões. Diminua o período ou a recorrência.`

/** Conta sem repetidos, como a request da prévia (`buildInvitePreviewRequest`). */
export function exceedsInviteStationLimit(stationIds: number[]): boolean {
  return new Set(stationIds).size > ALLOCATION_INVITE_MAX_STATIONS
}

export interface BuildInvitePreviewRequestInput {
  scheduleId: number
  stationIds: number[]
  mainDoctorId: number
  inviteeIds: number[]
  mode: AllocationInviteMode
}

export interface BuildInviteRequestInput extends BuildInvitePreviewRequestInput {
  formationId: number | null
  ourTeam: boolean
  userSponsorId: number | null
  /** Motivo da remoção do médico atual; ignorado fora do `REPLACE_NOW` (D104). */
  changeReasons?: ChangeReasons
}

/** Médico do passo 1 primeiro, sem repetidos; plantões sem repetidos, na ordem recebida. */
export function buildInvitePreviewRequest(
  input: BuildInvitePreviewRequestInput
): AllocationInvitePreviewRequest {
  return {
    scheduleId: input.scheduleId,
    stationIds: Array.from(new Set(input.stationIds)),
    doctorIds: Array.from(new Set([input.mainDoctorId, ...input.inviteeIds])),
    mode: input.mode
  }
}

/**
 * Mesmo corpo da prévia; só a lista branca da D78 no `allocationPayload`. `changeReasons` só vai
 * no `REPLACE_NOW` (D104): `VACANCY`/`REPLACE_KEEP` não removem ninguém na criação.
 */
export function buildInviteRequest(input: BuildInviteRequestInput): CreateAllocationInviteRequest {
  const sendsReasons = inviteRemovesCurrentDoctorNow(input.mode) && !!input.changeReasons
  return {
    ...buildInvitePreviewRequest(input),
    allocationPayload: {
      dsPaymentDoctorFormationId: input.formationId,
      ourTeam: input.ourTeam,
      userSponsorId: input.userSponsorId
    },
    ...(sendsReasons ? { changeReasons: input.changeReasons } : {})
  }
}

const DEFAULT_UNIT_TIMEZONE = '-03:00'

/**
 * Fuso da unidade para o `date-fns-tz`: `station.timezone` vem como offset (`-03:00`), mas IANA
 * também serve. Vazio/ausente cai em `-03:00` (mesma convenção do `tzOf` da grade).
 */
export function resolveUnitTimezone(timezone?: string | null): string {
  const trimmed = timezone?.trim()
  return trimmed || DEFAULT_UNIT_TIMEZONE
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.length > 0
}

function parseIso(isoUtc: string | null): Date | null {
  if (!isoUtc) return null
  const date = new Date(isoUtc)
  return Number.isNaN(date.getTime()) ? null : date
}

/** `27/10 às 07:00` na hora da unidade; null sem vencimento ou com data inválida. */
export function formatInviteDeadline(
  isoUtc: string | null,
  timezone?: string | null
): string | null {
  const date = parseIso(isoUtc)
  if (!date) return null
  return formatInTimeZone(date, resolveUnitTimezone(timezone), "dd/MM 'às' HH:mm")
}

export interface InviteDeadlineParts {
  /** `27/10 às 07:00` na hora da unidade (o template destaca em negrito). */
  when: string
  reason: 'no início do primeiro plantão' | '48 horas depois do envio'
}

/**
 * O vencimento é min(início do 1º plantão elegível, envio + 48h) (D13, D53). A prévia manda só
 * o resultado; o motivo é inferido comparando com a hora de parede do primeiro plantão elegível.
 * Devolve em partes para o template montar a frase com a data em negrito.
 */
export function splitInviteDeadline(
  preview: AllocationInvitePreview,
  timezone?: string | null
): InviteDeadlineParts | null {
  const expires = parseIso(preview.expiresAt)
  if (!expires) return null
  const tz = resolveUnitTimezone(timezone)
  const when = formatInTimeZone(expires, tz, "dd/MM 'às' HH:mm")
  const firstStart = preview.stations
    .filter((s) => preview.eligibleStationIds.includes(s.stationId))
    .map((s) => s.startHour)
    .filter(isNonEmptyString)
    .sort()[0]
  const expiresWall = formatInTimeZone(expires, tz, "yyyy-MM-dd'T'HH:mm")
  const reason =
    firstStart && firstStart.slice(0, 16) === expiresWall
      ? 'no início do primeiro plantão'
      : '48 horas depois do envio'
  return { when, reason }
}

/**
 * Plantões que ficam fora do convite por outro motivo que não o início (convite em andamento ou
 * vaga incompatível: aberta ou com médico, D28). Nunca negativo.
 */
export function countExcludedStations(preview: AllocationInvitePreview): number {
  const excluded =
    preview.stations.length - preview.eligibleStationIds.length - preview.startedStationIds.length
  return Math.max(0, excluded)
}

/** Aviso dos plantões que ficam fora do convite; null quando todos entram. */
export function buildStartedNotice(preview: AllocationInvitePreview): string | null {
  const eligible = preview.eligibleStationIds.length
  const started = preview.startedStationIds.length
  const excluded = countExcludedStations(preview)
  if (eligible === 0) {
    if (started > 0 && started === preview.stations.length) {
      return 'Todos os plantões já começaram. Use a alocação direta.'
    }
    return 'Nenhum plantão pode entrar neste convite: já começou, já tem convite em andamento ou não combina com a vaga (aberta ou com médico).'
  }
  const parts: string[] = []
  if (started > 0) {
    const verb = started === 1 ? 'começou e fica' : 'começaram e ficam'
    const pronoun = started === 1 ? 'ele' : 'eles'
    parts.push(
      `${pluralizeStations(started)} já ${verb} fora do convite. Para ${pronoun}, use a alocação direta.`
    )
  }
  if (excluded > 0) {
    const rest =
      excluded === 1
        ? 'fica fora do convite: já tem convite em andamento ou não combina'
        : 'ficam fora do convite: já têm convite em andamento ou não combinam'
    parts.push(`${pluralizeStations(excluded)} ${rest} com a vaga (aberta ou com médico).`)
  }
  return parts.length ? parts.join(' ') : null
}

const REASON_LABELS: Record<AllocationInviteIssueReason, string> = {
  OVERLAP: 'conflito de agenda',
  CONSECUTIVE_HOURS: 'limite de horas consecutivas',
  NO_LINK: 'sem vínculo com a escala',
  POLICY_BLOCKED: 'bloqueio da política',
  QUALIFICATION: 'qualificação pendente'
}

const REASON_ORDER: AllocationInviteIssueReason[] = [
  'OVERLAP',
  'CONSECUTIVE_HOURS',
  'NO_LINK',
  'POLICY_BLOCKED',
  'QUALIFICATION'
]

/** `dd/MM` direto da hora de parede (`YYYY-MM-DDTHH:mm:ss`), que já está no fuso da unidade. */
function wallDate(startHour: string): string {
  return `${startHour.slice(8, 10)}/${startHour.slice(5, 7)}`
}

/** Datas `dd/MM` em ordem cronológica (pela hora de parede completa), sem repetidas. */
function orderedWallDates(startHours: string[]): string[] {
  return Array.from(new Set([...startHours].sort().map(wallDate)))
}

function joinDates(dates: string[]): string {
  if (dates.length <= 1) return dates.join('')
  return `${dates.slice(0, -1).join(', ')} e ${dates[dates.length - 1]}`
}

/**
 * Uma frase por médico com aviso (D23: avisa, não bloqueia): motivos agrupados e as datas
 * (`dd/MM`, cronológicas e sem repetir) dos plantões afetados. Plantão sem data conhecida não
 * entra na lista de datas; motivo sem data vira só o rótulo. Médicos sem aviso conhecido não
 * aparecem.
 */
export function buildDoctorIssueNotices(
  preview: AllocationInvitePreview,
  doctorName: (doctorId: number) => string
): string[] {
  const startOf = new Map(preview.stations.map((s) => [s.stationId, s.startHour]))
  return preview.doctors.flatMap((doctor) => {
    if (doctor.issues.length === 0) return []
    const byReason = new Map<AllocationInviteIssueReason, string[]>()
    for (const issue of doctor.issues) {
      const startHour = startOf.get(issue.stationId)
      for (const reason of issue.reasons) {
        const list = byReason.get(reason) ?? []
        if (isNonEmptyString(startHour)) list.push(startHour)
        byReason.set(reason, list)
      }
    }
    const parts = REASON_ORDER.filter((r) => byReason.has(r)).map((r) => {
      const dates = orderedWallDates(byReason.get(r) ?? [])
      return dates.length ? `${REASON_LABELS[r]} em ${joinDates(dates)}` : REASON_LABELS[r]
    })
    if (parts.length === 0) return []
    return [`${doctorName(doctor.doctorId)}: ${parts.join('; ')}. Você pode confirmar mesmo assim.`]
  })
}

export function inviteSummaryText(doctorCount: number, stationCount: number): string {
  return `Você está convidando ${pluralizeDoctors(doctorCount)} para ${pluralizeStations(stationCount)}. Deseja continuar?`
}

/** Plantão com troca ou convite pendente: alocar, editar ou excluir cancela o pendente (D15, D54). */
export const hasPendingCommitment = (p: PendingCommitment): boolean =>
  p.hasPendingHandover || p.allocationInviteId != null

/** Falha da consulta de pendências: o aviso vira genérico e não bloqueia a ação (D54d). */
export const PENDING_CHECK_FAILED_NOTICE =
  'Não foi possível verificar trocas ou convites pendentes. Se houver, eles serão cancelados ao confirmar e os envolvidos avisados.'

/**
 * Aviso antes de editar ou excluir UM plantão (D8, D54d): diz o que cai e quem é avisado.
 * `null` = nada pendente.
 */
export function buildStationPendingNotice(pending: PendingCommitment[]): string | null {
  const hasInvite = pending.some((p) => p.allocationInviteId != null)
  const hasHandover = pending.some((p) => p.hasPendingHandover)
  if (hasInvite && hasHandover) {
    return 'O convite em andamento e a troca pendente serão cancelados, e os médicos envolvidos serão avisados.'
  }
  if (hasInvite) {
    return 'O convite em andamento será cancelado e os médicos convidados serão avisados.'
  }
  if (hasHandover) return 'A troca pendente será cancelada e os médicos envolvidos serão avisados.'
  return null
}
